/**
 * Per-environment configuration, read at run time.
 *
 * Production: `/config.js` sets `window.__ME_CONFIG__` before the app bundle
 * runs. The App Service writes that file at startup from its own app settings
 * (see public/startup.sh), so the same build runs in every environment. The
 * legacy deploy still replaces the `__REPLACE_ON_DEPLOYMENT_*` build values; a
 * build value it has not replaced counts as unset.
 *
 * Development, tests, E2E and demo builds: no `/config.js` is served, so each
 * value falls back to its `VITE_*` build variable (.env.development, the
 * scripts in scripts/, or vi.stubEnv in a test).
 *
 * Values are read on every call, not once at import, so a test that stubs a
 * variable after importing a module still sees its stub.
 *
 * NOTE: every `import.meta.env.X` below must stay a literal member access.
 * A bare `import.meta.env` makes Vite inline the whole env object into the
 * production bundle.
 */

const runtimeConfig = () =>
    (typeof window !== "undefined" && window.__ME_CONFIG__) || {};

const PLACEHOLDER_PREFIX = "__REPLACE_ON_DEPLOYMENT_";
const DEFAULT_TITLE = "Viedoc Me";

const isPlaceholder = (value) =>
    typeof value === "string" && value.startsWith(PLACEHOLDER_PREFIX);

const pick = (runtimeValue, buildValue) => {
    if (runtimeValue !== undefined) {
        return runtimeValue;
    }
    return isPlaceholder(buildValue) ? undefined : buildValue;
};

const withoutTrailingSlash = (value) =>
    typeof value === "string" && value.endsWith("/") ? value.slice(0, -1) : value;

const originList = (value) =>
    value
        .split(",")
        .map((origin) => withoutTrailingSlash(origin.trim()))
        .join(",");

let missingMeIdpUrlReported = false;

const reportMissingMeIdpUrl = (meIdpUrl) => {
    if (meIdpUrl || import.meta.env.DEV || missingMeIdpUrlReported) {
        return;
    }
    missingMeIdpUrlReported = true;
    console.error(
        "Me.Web configuration: no SubjectIdP URL is set, neither ME_WEB_ME_IDP_URL in /config.js nor a build value. Sign-in will fail.",
    );
};

export function getConfig() {
    const runtime = runtimeConfig();
    const meIdpUrl = withoutTrailingSlash(
        pick(runtime.meIdpUrl, import.meta.env.VITE_ME_IDP_URL),
    );
    reportMissingMeIdpUrl(meIdpUrl);
    return {
        apiBaseUrl: withoutTrailingSlash(
            pick(runtime.apiBaseUrl, import.meta.env.VITE_API_BASE_URL) ?? "",
        ),
        meIdpUrl,
        viedocShareBaseUrl: withoutTrailingSlash(
            pick(
                runtime.viedocShareBaseUrl,
                import.meta.env.VITE_VIEDOC_SHARE_BASE_URL,
            ),
        ),
        applicationInsightsConnectionString: pick(
            runtime.applicationInsightsConnectionString,
            import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
        ),
        designerOrigins: originList(
            pick(runtime.designerOrigins, import.meta.env.VITE_DESIGNER_ORIGINS) ?? "",
        ),
        title: runtime.title,
        // The release this deployment belongs to, when the deploy has set it.
        // Absent means absent: never a default that reads like a release.
        releaseLabel: runtime.releaseLabel,
        releaseDisplay: runtime.releaseDisplay,
    };
}

export function getPageTitle(currentTitle) {
    const { title } = getConfig();
    if (title) {
        return title;
    }
    return isPlaceholder(currentTitle) ? DEFAULT_TITLE : currentTitle;
}
